import Model from './BaseModel'
import i18n from '@/plugins/i18n'
import Address from './Address'
import BankAccount from './BankAccount'
import Customer from './Customer'
import moment from 'moment'
import ResellerType from './ResellerType'

const NEW = 'new'
export default class Reseller extends Model {
    resourceName() {
        return 'resellers'
    }

    fields() {
        return [
            'id',
            'billable',
            'connection',
            'schema',
            'tenant_code',
            'legal_name',
            'trade_name',
            'cpf',
            'cnpj',
            'cnae_id',
            'contact_name',
            'email',
            'phone',
            'website',
            'cplug_domain',
            'internal_user',
            'manager_id',
            'erp_schema',
            'observation',
            'created_at',
            'last_customer',
            'has_support',
            'reseller_type_id',
            'contract_url',
            'block_reason',
            'indicators',
            'status',
            'category',
            'has_fixed_category',
            'deleted_at',
            'defaulter',
            'inscricao_estadual',
            'inscricao_municipal',
            'self_managed',
            'last_access',
            'po',
            'notify_customers',
            'canceled_customers',
            'billing_cplug',
        ]
    }

    relationships() {
        return {
            addresses: new Address(),
            bankAccounts: new BankAccount(),
            bankAccountActive: new BankAccount(),
            customers: new Customer(),
            resellerType: new ResellerType(),
        }
    }

    dates() {
        return {
            created_at: 'YYYY-MM-DD',
            deleted_at: 'YYYY-MM-DD',
        }
    }

    computed() {
        return {
            partnerType(reseller) {
                return reseller.resellerType.name
            },

            lastAccess(reseller) {
                const date = new Date(reseller.last_access)
                if (isNaN(date)) {
                    return ''
                }

                return `${date.getDate()}/${date.getMonth() +
                    1}/${date.getFullYear()}`
            },

            document(customer) {
                if (customer.cnpj) {
                    return customer.cnpj.replace(
                        /(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/,
                        '$1.$2.$3/$4-$5',
                    )
                } else if (customer.cpf) {
                    return customer.cpf.replace(
                        /(\d{3})(\d{3})(\d{3})(\d{2})/,
                        '$1.$2.$3-$4',
                    )
                }

                return ''
            },

            lastActivation(reseller) {
                let lastActivation = reseller.created_at

                if (reseller.last_customer) {
                    lastActivation = moment(reseller.last_customer.active_at)
                }

                let now = moment()
                return now.diff(lastActivation, 'days')
            },

            lastActivationDate(reseller) {
                if (reseller.last_customer) {
                    return moment(reseller.last_customer.active_at)
                }

                return null
            },

            formattedStatus(reseller) {
                return i18n.t(
                    `resources.reseller.getters.status.${reseller.status}`,
                )
            },

            deleted(reseller) {
                return reseller.deleted_at && reseller.deleted_at.isValid()
            },
        }
    }

    activate() {
        return this.makePersistRequest({
            url: `${this.getSelfUrl()}/activate`,
            method: 'PATCH',
        })
    }

    fixCategory(categoryId) {
        return this.makePersistRequest({
            url: `${this.getSelfUrl()}/fixCategory`,
            method: 'PATCH',
            data: { category_id: categoryId },
        })
    }
    storeSupplier() {
        return this.makePersistRequest({
            url: `${this.getSelfUrl()}/store-supplier`,
            method: 'GET',
        })
    }
    notifyCustomers() {
        return this.makePersistRequest({
            url: `${this.getSelfUrl()}/notify-customers`,
            method: 'PUT',
            data: {
                notify_customers: !this.notify_customers,
            },
        })
    }

    get tableColumns() {
        return [
            {
                text: i18n.t('partner.index.table.company'),
                value: 'trade_name',
                width: 350,
            },
            {
                text: i18n.t('partner.index.table.type'),
                value: 'partnerType',
                sortable: false,
            },
            {
                text: i18n.t('partner.index.table.lastAccess'),
                value: 'lastAccess',
                sortable: false,
            },
            {
                text: 'Status',
                value: 'status',
                sortable: true,
            },
            {
                text: i18n.t('resources.reseller.attributes.customers'),
                value: 'customers',
                sortable: true,
            },
            {
                text: i18n.t('resources.reseller.attributes.mmr'),
                value: 'mmr',
                sortable: true,
            },
            {
                text: i18n.t('resources.reseller.attributes.lastActivation'),
                value: 'lastActivation',
                sortable: true,
            },
            {
                text: i18n.t('resources.reseller.attributes.customers_canceled'),
                value: 'canceled_customers',
                sortable: true,
            },
            {
                text: i18n.t('resources.reseller.attributes.notify_customers'),
                value: 'notify_customers',
                sortable: false,
            },
            {
                text: i18n.t('tables.actions'),
                value: 'action',
                sortable: false,
                align: 'end',
            },
        ]
    }

    get actions() {
        return [
            {
                type: 'menu',
                hide: this.status === NEW,
                actions: [
                    {
                        type: 'fixCategory',
                        label: i18n.t('actions.reseller.fixCategory.label'),
                        disabled: this.billable,
                    },
                    {
                        type: 'storeSupplier',
                        label: i18n.t('actions.reseller.storeSupplier.label'),
                        disabled: this.billable,
                    },
                    {
                        type: 'notifyCustomers',
                        label: this.notify_customers
                            ? i18n.t('actions.reseller.notifyCustomers.label2')
                            : i18n.t('actions.reseller.notifyCustomers.label'),
                        disabled: !this.billable,
                    },
                ],
            },
            {
                type: 'activate',
                label: i18n.t('actions.reseller.activate.label'),
                icon: 'mdi-check',
                color: 'green',
                hide: this.status !== NEW,
            },
            {
                type: 'show',
                to: {
                    name: 'reseller.show',
                    params: { id: this.id },
                },
                hide: this.status === NEW,
            },
            {
                type: 'edit',
                to: {
                    name: 'reseller.edit',
                    params: { id: this.id },
                },
            },
            {
                type: 'delete',
                disabled: this.id == 1,
                hide: this.deleted,
            },
            {
                type: 'restore',
                hide: !this.deleted,
            },
        ]
    }
}